import { useState } from 'react'
import { useGameStore } from '../stores/gameStore'
import { SCHOOLS, QUALITIES, TRAINING_OPTIONS, type Talent, type TalentQuality, type TalentSchool } from '../data/talentData'
import { HISTORICAL_TALENTS, type HistoricalTalent } from '../data/historicalTalents'
import { CharacterCard } from './CharacterCard'
import type { Character } from '../types/character'
import './TalentPanel.css'

export function TalentPanel() {
  const {
    talents,
    availableTalents,
    recruitCooldown,
    resources,
    recruitTalent,
    refreshAvailableTalents,
    appointTalent,
    dismissTalent,
    startTraining,
  } = useGameStore()

  const [selectedTalent, setSelectedTalent] = useState<Talent | null>(null)
  const [activeTab, setActiveTab] = useState<'my' | 'recruit' | 'training'>('my')
  const [showTrainingModal, setShowTrainingModal] = useState(false)
  const [showBiographyModal, setShowBiographyModal] = useState(false)
  const [selectedHistorical, setSelectedHistorical] = useState<HistoricalTalent | null>(null)
  const [showCharacterCard, setShowCharacterCard] = useState(false)

  const getQualityColor = (quality: TalentQuality) => {
    return QUALITIES.find(q => q.id === quality)?.color || '#888'
  }

  const getSchoolName = (school: TalentSchool) => {
    return SCHOOLS.find(s => s.id === school)?.name || school
  }

  const getSchoolColor = (school: TalentSchool) => {
    return SCHOOLS.find(s => s.id === school)?.color || '#888'
  }

  const handleRecruit = (talentId: string) => {
    const success = recruitTalent(talentId)
    if (!success) {
      alert('招募失败：金钱或声望不足，或正在冷却中')
    }
  }

  const handleAppoint = (talentId: string, position: string | null) => {
    appointTalent(talentId, position)
  }

  const handleTraining = (trainingId: string) => {
    if (!selectedTalent) return
    const success = startTraining(selectedTalent.id, trainingId)
    if (success) {
      setShowTrainingModal(false)
      alert('进修已开始！')
    } else {
      alert('进修失败：资源不足或该人才正在进修中')
    }
  }

  const handleShowBiography = (talent: Talent) => {
    // 检查是否是历史人物
    if (talent.isHistorical && talent.historicalId) {
      const historical = HISTORICAL_TALENTS.find(h => h.id === talent.historicalId)
      if (historical) {
        setSelectedHistorical(historical)
        setShowBiographyModal(true)
      }
    }
  }

  const isHistoricalTalent = (talent: Talent): boolean => {
    return !!talent.isHistorical && !!talent.historicalId
  }

  // 将 Talent 转换为 Character 类型
  const convertTalentToCharacter = (talent: Talent): Character => {
    return {
      id: talent.id,
      name: talent.fullName || talent.surname + talent.name,
      factionId: 'player',
      identity: 'scholar',
      gender: 'male',
      birthDate: { year: 20, month: 1, day: 1 },
      status: 'active',
      abilities: {
        intelligence: talent.intelligence,
        charisma: talent.charisma,
        military: talent.military,
        administration: talent.politics,
        diplomacy: talent.charisma,
        intrigue: talent.intelligence,
      },
      traits: [],
      loyalty: talent.loyalty,
      reputation: 60,
      position: talent.position ? { title: talent.position, rank: 3 } : undefined,
      relations: {
        childrenIds: [],
        siblingIds: [],
        friendIds: [],
        enemyIds: [],
      },
      history: [],
    }
  }

  return (
    <div className="talent-panel">
      <h2 className="panel-title">招贤纳士</h2>

      <div className="talent-tabs">
        <button
          className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`}
          onClick={() => setActiveTab('my')}
        >
          麾下人才 ({talents.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'recruit' ? 'active' : ''}`}
          onClick={() => setActiveTab('recruit')}
        >
          寻访贤才 ({availableTalents.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'training' ? 'active' : ''}`}
          onClick={() => setActiveTab('training')}
        >
          进修培养
        </button>
      </div>

      {activeTab === 'my' && (
        <div className="talent-content">
          {talents.length === 0 ? (
            <div className="empty-state">
              <p>暂无人才，请前往"寻访贤才"招募</p>
            </div>
          ) : (
            <>
              <div className="talent-list">
                {talents.map((talent) => (
                  <div
                    key={talent.id}
                    className={`talent-card ${selectedTalent?.id === talent.id ? 'selected' : ''}`}
                    onClick={() => setSelectedTalent(talent)}
                  >
                    <div className="talent-portrait" style={{ borderColor: getQualityColor(talent.quality) }}>
                      {talent.surname}
                    </div>
                    <div className="talent-basic">
                      <span className="talent-name">{talent.fullName || talent.surname + talent.name}</span>
                      <span className="talent-school" style={{ color: getSchoolColor(talent.school) }}>
                        {getSchoolName(talent.school)}
                      </span>
                      {talent.position && (
                        <span className="talent-position">{talent.position}</span>
                      )}
                    </div>
                    <div className="talent-loyalty">
                      <span className="loyalty-label">忠诚</span>
                      <div className="loyalty-bar">
                        <div
                          className="loyalty-fill"
                          style={{ width: `${talent.loyalty}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedTalent && (
                <div className="talent-detail">
                  <div className="detail-header">
                    <div
                      className="detail-portrait"
                      style={{ borderColor: getQualityColor(selectedTalent.quality) }}
                    >
                      {selectedTalent.surname}
                    </div>
                    <div className="detail-basic">
                      <h3>{selectedTalent.fullName || selectedTalent.surname + selectedTalent.name}</h3>
                      <div className="quality-row">
                        <span className="detail-quality" style={{ color: getQualityColor(selectedTalent.quality) }}>
                          {QUALITIES.find(q => q.id === selectedTalent.quality)?.name}
                        </span>
                        <span className="quality-desc">
                          {QUALITIES.find(q => q.id === selectedTalent.quality)?.description}
                        </span>
                      </div>
                      <span className="detail-school" style={{ color: getSchoolColor(selectedTalent.school) }}>
                        {getSchoolName(selectedTalent.school)}
                      </span>
                      {selectedTalent.position ? (
                        <span className="detail-position">现任：{selectedTalent.position}</span>
                      ) : (
                        <span className="detail-position unassigned">未任命</span>
                      )}
                    </div>
                  </div>

                  <p className="detail-description">{selectedTalent.description}</p>

                  <div className="detail-stats">
                    <h4>能力值</h4>
                    <div className="stats-grid">
                      <div className="stat-item">
                        <span className="stat-name">政治</span>
                        <div className="stat-bar">
                          <div
                            className="stat-fill politics"
                            style={{ width: `${selectedTalent.politics}%` }}
                          />
                        </div>
                        <span className="stat-value">{selectedTalent.politics}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-name">军事</span>
                        <div className="stat-bar">
                          <div
                            className="stat-fill military"
                            style={{ width: `${selectedTalent.military}%` }}
                          />
                        </div>
                        <span className="stat-value">{selectedTalent.military}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-name">智谋</span>
                        <div className="stat-bar">
                          <div
                            className="stat-fill intelligence"
                            style={{ width: `${selectedTalent.intelligence}%` }}
                          />
                        </div>
                        <span className="stat-value">{selectedTalent.intelligence}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-name">魅力</span>
                        <div className="stat-bar">
                          <div
                            className="stat-fill charisma"
                            style={{ width: `${selectedTalent.charisma}%` }}
                          />
                        </div>
                        <span className="stat-value">{selectedTalent.charisma}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-actions">
                    <button
                      className="action-btn biography"
                      onClick={() => handleShowBiography(selectedTalent)}
                      disabled={!isHistoricalTalent(selectedTalent)}
                      title={isHistoricalTalent(selectedTalent) ? '查看人物生平故事' : '随机生成人物暂无生平记录'}
                    >
                      {isHistoricalTalent(selectedTalent) ? '查看生平' : '暂无生平'}
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => setShowCharacterCard(true)}
                    >
                      人物卡片
                    </button>
                    {selectedTalent.position ? (
                      <button
                        className="action-btn remove"
                        onClick={() => handleAppoint(selectedTalent.id, null)}
                      >
                        罢免职务
                      </button>
                    ) : (
                      <>
                        <button
                          className="action-btn appoint"
                          onClick={() => handleAppoint(selectedTalent.id, '丞相')}
                        >
                          任命为丞相
                        </button>
                        <button
                          className="action-btn appoint"
                          onClick={() => handleAppoint(selectedTalent.id, '将军')}
                        >
                          任命为将军
                        </button>
                      </>
                    )}
                    <button
                      className="action-btn train"
                      onClick={() => setShowTrainingModal(true)}
                      disabled={selectedTalent.isTraining}
                    >
                      {selectedTalent.isTraining ? '进修中...' : '送去进修'}
                    </button>
                    <button
                      className="action-btn dismiss"
                      onClick={() => {
                        if (confirm('确定要解雇这位人才吗？')) {
                          dismissTalent(selectedTalent.id)
                          setSelectedTalent(null)
                        }
                      }}
                    >
                      解雇
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'recruit' && (
        <div className="recruit-section">
          <div className="recruit-header">
            <div className="recruit-info">
              <h3>寻访贤才</h3>
              <p>消耗资源招募天下贤才</p>
              <div className="recruit-cost">
                <span>💰 100金</span>
                <span>🏆 5声望</span>
              </div>
              {recruitCooldown > 0 && (
                <span className="cooldown">冷却中：{recruitCooldown}个月</span>
              )}
            </div>
            <button
              className="refresh-btn"
              onClick={refreshAvailableTalents}
              disabled={recruitCooldown > 0}
            >
              刷新人才
            </button>
          </div>

          <div className="available-talents">
            {availableTalents.map((talent) => (
              <div key={talent.id} className="talent-card available">
                <div
                  className="talent-portrait"
                  style={{ borderColor: getQualityColor(talent.quality) }}
                >
                  {talent.surname}
                  {talent.isHistorical && (
                    <span 
                      className="historical-badge"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (talent.historicalId) {
                          const historical = HISTORICAL_TALENTS.find(h => h.id === talent.historicalId)
                          if (historical) {
                            setSelectedHistorical(historical)
                            setShowBiographyModal(true)
                          }
                        }
                      }}
                      title="点击查看生平"
                    >
                      史
                    </span>
                  )}
                </div>
                <div className="talent-basic">
                  <span className="talent-name">{talent.fullName || talent.surname + talent.name}</span>
                  <span className="talent-school" style={{ color: getSchoolColor(talent.school) }}>
                    {getSchoolName(talent.school)}
                  </span>
                  <span className="talent-quality" style={{ color: getQualityColor(talent.quality) }}>
                    {QUALITIES.find(q => q.id === talent.quality)?.name}
                  </span>
                  {talent.isHistorical && (
                    <span 
                      className="historical-tag"
                      onClick={() => {
                        if (talent.historicalId) {
                          const historical = HISTORICAL_TALENTS.find(h => h.id === talent.historicalId)
                          if (historical) {
                            setSelectedHistorical(historical)
                            setShowBiographyModal(true)
                          }
                        }
                      }}
                    >
                      名留青史
                    </span>
                  )}
                </div>
                <div className="talent-preview">
                  <span>政{talent.politics}</span>
                  <span>军{talent.military}</span>
                  <span>智{talent.intelligence}</span>
                  <span>魅{talent.charisma}</span>
                </div>
                <button
                  className="recruit-btn"
                  onClick={() => handleRecruit(talent.id)}
                  disabled={resources.money < 100 || resources.prestige < 5 || recruitCooldown > 0}
                >
                  招募
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'training' && (
        <div className="training-section">
          <h3>进修培养</h3>
          <p className="training-desc">选择人才进行进修，提升能力或转换流派</p>

          <div className="training-list">
            {TRAINING_OPTIONS.map((option) => (
              <div key={option.id} className="training-option">
                <div className="training-header">
                  <h4>{option.name}</h4>
                  <span className="training-duration">{option.duration}个月</span>
                </div>
                <p className="training-description">{option.description}</p>
                <div className="training-cost">
                  <span>💰 {option.cost.money}金</span>
                  <span>🏆 {option.cost.prestige}声望</span>
                </div>
                {option.effects.schoolChange && (
                  <div className="training-effect">
                    转修{SCHOOLS.find(s => s.id === option.effects.schoolChange)?.name}
                  </div>
                )}
                {option.effects.statBoost && (
                  <div className="training-effect">
                    {option.effects.statBoost.stat === 'politics' && '政治'}
                    {option.effects.statBoost.stat === 'military' && '军事'}
                    {option.effects.statBoost.stat === 'intelligence' && '智谋'}
                    {option.effects.statBoost.stat === 'charisma' && '魅力'}
                    +{option.effects.statBoost.value}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showTrainingModal && selectedTalent && (
        <div className="modal-overlay" onClick={() => setShowTrainingModal(false)}>
          <div className="training-modal" onClick={e => e.stopPropagation()}>
            <h3>选择进修项目</h3>
            <p>为 {selectedTalent.fullName || selectedTalent.surname + selectedTalent.name} 选择进修方向</p>

            <div className="training-options">
              {TRAINING_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  className="training-select-btn"
                  onClick={() => handleTraining(option.id)}
                  disabled={resources.money < option.cost.money || resources.prestige < option.cost.prestige}
                >
                  <span className="training-name">{option.name}</span>
                  <span className="training-cost">
                    💰{option.cost.money} 🏆{option.cost.prestige}
                  </span>
                </button>
              ))}
            </div>

            <button className="close-modal" onClick={() => setShowTrainingModal(false)}>
              取消
            </button>
          </div>
        </div>
      )}

      {showCharacterCard && selectedTalent && (
        <CharacterCard
          character={convertTalentToCharacter(selectedTalent)}
          onClose={() => setShowCharacterCard(false)}
        />
      )}

      {showBiographyModal && selectedHistorical && (
        <div className="modal-overlay" onClick={() => setShowBiographyModal(false)}>
          <div className="biography-modal" onClick={e => e.stopPropagation()}>
            <div className="biography-header">
              <h2>{selectedHistorical.fullName}</h2>
              <span className="biography-era">{selectedHistorical.era}</span>
              <span className="biography-lifespan">
                {selectedHistorical.birthYear < 0 ? `公元前${Math.abs(selectedHistorical.birthYear)}年` : `公元${selectedHistorical.birthYear}年`}
                {' - '}
                {selectedHistorical.deathYear < 0 ? `公元前${Math.abs(selectedHistorical.deathYear)}年` : `公元${selectedHistorical.deathYear}年`}
              </span>
            </div>

            <div className="biography-tags">
              {selectedHistorical.tags.map((tag, index) => (
                <span key={index} className="biography-tag">{tag}</span>
              ))}
            </div>

            <div className="biography-content">
              <section className="biography-section">
                <h4>简介</h4>
                <p>{selectedHistorical.biography.summary}</p>
              </section>

              <section className="biography-section">
                <h4>早年经历</h4>
                <p>{selectedHistorical.biography.earlyLife}</p>
              </section>

              <section className="biography-section">
                <h4>主要事迹</h4>
                <ul>
                  {selectedHistorical.biography.majorEvents.map((event, index) => (
                    <li key={index}>{event}</li>
                  ))}
                </ul>
              </section>

              <section className="biography-section">
                <h4>主要成就</h4>
                <ul>
                  {selectedHistorical.biography.achievements.map((achievement, index) => (
                    <li key={index}>{achievement}</li>
                  ))}
                </ul>
              </section>

              <section className="biography-section">
                <h4>历史评价</h4>
                <p>{selectedHistorical.biography.legacy}</p>
              </section>

              {selectedHistorical.biography.quotes && selectedHistorical.biography.quotes.length > 0 && (
                <section className="biography-section quotes">
                  <h4>名言</h4>
                  {selectedHistorical.biography.quotes.map((quote, index) => (
                    <blockquote key={index}>"{quote}"</blockquote>
                  ))}
                </section>
              )}
            </div>

            <button className="close-modal" onClick={() => setShowBiographyModal(false)}>
              关闭
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
